import { useState, useEffect } from "react";
import { Loader2, FileCheck2, Check, X, RotateCcw, AlertTriangle, FileText, Bot } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, EmptyState } from "@/components/admin/ui";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { api, errMsg, API } from "@/lib/api";
import { formatGs, formatDateTime, COMP_STATUS } from "@/lib/format";

const ComprobanteImage = ({ comp }) => {
  const [url, setUrl] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let blobUrl;
    const token = localStorage.getItem("iv_token");
    fetch(`${API}/admin/comprobantes/${comp.id}/file?auth=${token}`)
      .then((r) => { if (!r.ok) throw new Error(); return r.blob(); })
      .then((b) => { blobUrl = URL.createObjectURL(b); setUrl(blobUrl); })
      .catch(() => setErr(true));
    return () => blobUrl && URL.revokeObjectURL(blobUrl);
  }, [comp.id]);

  if (err) return <div className="text-sm text-slate-400 py-8 text-center">No se pudo cargar el archivo.</div>;
  if (!url) return <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-indigo-600" /></div>;
  if (comp.ext === "pdf") return <iframe src={url} title="comprobante" className="w-full h-[420px] rounded-lg border" />;
  return <img src={url} alt="Comprobante" className="w-full rounded-lg border max-h-[420px] object-contain" />;
};

const Comprobantes = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState("");

  const load = () => { setLoading(true); api.get("/admin/comprobantes").then((r) => setItems(r.data)).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);

  const review = async (status) => {
    try {
      await api.patch(`/admin/comprobantes/${selected.id}`, { status, note });
      toast.success("Comprobante actualizado");
      setSelected(null); setNote(""); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div>
      <PageHeader title="Comprobantes" subtitle="Revisá y aprobá los comprobantes de pago" />
      {loading ? <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-indigo-600" /></div>
        : items.length === 0 ? <EmptyState icon={FileCheck2} text="Sin comprobantes." />
        : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map((c) => (
              <div key={c.id} className="bg-white rounded-xl border border-slate-200 p-4" data-testid={`comp-${c.order_number}`}>
                <div className="flex justify-between items-start mb-2">
                  <p className="font-semibold text-slate-900">Pedido #{c.order_number}</p>
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${COMP_STATUS[c.analysis_status]?.color}`}>{COMP_STATUS[c.analysis_status]?.label}</span>
                </div>
                <p className="text-xs text-slate-500">{c.customer_name} · {c.whatsapp}</p>
                <p className="text-sm font-medium text-slate-800 mt-1">Esperado: {formatGs(c.amount_expected)}</p>
                <p className="text-xs text-slate-400">{formatDateTime(c.created_at)}</p>
                {c.is_duplicate && <p className="mt-2 text-xs text-rose-600 flex items-center gap-1"><AlertTriangle className="h-3.5 w-3.5" /> Comprobante duplicado</p>}
                {c.ai_analysis && <p className="mt-1 text-xs text-indigo-600 flex items-center gap-1"><Bot className="h-3.5 w-3.5" /> IA: {c.ai_analysis.recomendacion}</p>}
                <Button variant="outline" size="sm" className="rounded-lg w-full mt-3" onClick={() => { setSelected(c); setNote(""); }} data-testid={`review-${c.order_number}`}><FileText className="h-4 w-4 mr-1" /> Ver y revisar</Button>
              </div>
            ))}
          </div>
        )}

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          {selected && (
            <>
              <DialogHeader><DialogTitle>Comprobante · Pedido #{selected.order_number}</DialogTitle></DialogHeader>
              <ComprobanteImage comp={selected} />
              <div className="text-sm space-y-1.5 mt-2">
                <div className="flex justify-between"><span className="text-slate-500">Cliente</span><span className="font-medium">{selected.customer_name}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Monto esperado</span><span className="font-medium">{formatGs(selected.amount_expected)}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Modo análisis</span><span className="font-medium capitalize">{selected.analysis_mode}</span></div>
              </div>
              {selected.ai_analysis && (
                <div className="rounded-lg bg-indigo-50 p-3 text-xs mt-2">
                  <p className="font-semibold text-indigo-700 mb-1 flex items-center gap-1"><Bot className="h-3.5 w-3.5" /> Análisis automático (IA)</p>
                  <div className="space-y-0.5 text-slate-600">
                    {Object.entries(selected.ai_analysis).map(([k, v]) => (
                      <div key={k} className="flex justify-between gap-2"><span className="text-slate-400">{k}</span><span className="text-right font-medium">{String(v)}</span></div>
                    ))}
                  </div>
                </div>
              )}
              <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Observación (opcional, ej: el comprobante no corresponde al valor)" className="rounded-lg mt-2" data-testid="review-note" />
              <div className="grid grid-cols-3 gap-2 mt-2">
                <Button onClick={() => review("aprobado")} className="rounded-lg bg-emerald-600 hover:bg-emerald-700" data-testid="approve-btn"><Check className="h-4 w-4 mr-1" /> Aprobar</Button>
                <Button onClick={() => review("rechazado")} variant="outline" className="rounded-lg text-rose-600 border-rose-200 hover:bg-rose-50" data-testid="reject-btn"><X className="h-4 w-4 mr-1" /> Rechazar</Button>
                <Button onClick={() => review("solicitar_nuevo")} variant="outline" className="rounded-lg" data-testid="request-btn"><RotateCcw className="h-4 w-4 mr-1" /> Nuevo</Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Comprobantes;
